import {
  type Session,
  type SessionDTO,
  type SessionRecap,
  type SessionRecapDTO,
  type SessionStatusResponse,
  type SessionStatusResponseDTO,
  type MusicSources,
  type MusicSourcesDTO,
  type VideoNode,
  type VideoNodeDTO,
  type AudioNode,
  type AudioNodeDTO,
  type TextNode,
  type TextNodeDTO,
  type SessionBlockDTO,
  type SessionBlock,
  type MediaScriptDTO,
  type MediaScript,
  type MediaNodeDTO,
  type MediaNode,
  type ExerciseInfo,
  type ExerciseInfoDTO,
  type SessionBlockParameters,
  type SessionBlockParametersDTO,
} from "../../domain";
import { posixTimestampToUnix } from "../../utils";
import { type DomainMapper } from "./index";

export const sessionMapper: DomainMapper<Session, SessionDTO> = {
  toDomain: sessionDto => ({
    id: sessionDto.session_log_id,
    key: sessionDto.session_template_key,
    earliestStartAt: new Date(posixTimestampToUnix(sessionDto.earliest_start_at_timestamp)),
    latestEndAt: new Date(posixTimestampToUnix(sessionDto.latest_end_at_timestamp)),
    scheduledAt: new Date(posixTimestampToUnix(sessionDto.scheduled_at_timestamp)),
    week: sessionDto.week_in_treatment_since_start,
    currentNumber: sessionDto.session_number,
    duration: sessionDto.overall_duration_in_s,
    status: sessionDto.status,
    equipment: sessionDto.equipment.toSorted((a, b) => a.localeCompare(b)),
    exerciseInfo: sessionDto.exercise_info.map(exerciseInfoMapper.toDomain),
    blocks: sessionDto.session_flow.map(sessionBlockMapper.toDomain),
    sessionAppendix: sessionDto.session_appendix.map(sessionBlockMapper.toDomain),
    exerciseNum: sessionDto.exercise_num,
    phaseWeek: sessionDto.phase_week,
  }),
};

export const exerciseInfoMapper: DomainMapper<ExerciseInfo, ExerciseInfoDTO> = {
  toDomain: exerciseInfoDTO => ({
    routineLogId: exerciseInfoDTO.routine_log_id,
    routineTemplateKey: exerciseInfoDTO.routine_template_key,
    exerciseTemplateKey: exerciseInfoDTO.exercise_template_key,
    laterality: exerciseInfoDTO.laterality,
    hasLaterality: exerciseInfoDTO.has_laterality,
    numberOfSets: exerciseInfoDTO.number_of_sets,
    routineMetric: exerciseInfoDTO.routine_metric,
    numberOfRepetitions: exerciseInfoDTO.number_of_repetitions,
    minAllowedSecPerRep: exerciseInfoDTO.min_allowed_sec_per_rep,
    holdingTimeInS: exerciseInfoDTO.holding_time_in_s,
    breakDurationInS: exerciseInfoDTO.break_duration_in_s,
  }),
};

export const sessionRecapMapper: DomainMapper<SessionRecap, SessionRecapDTO> = {
  toDomain: sessionDto => ({
    totalTimeInSessionInS: sessionDto.total_time_in_session_in_s,
    activeTimeInSessionInS: sessionDto.active_time_in_session_in_s,
    exercises: sessionDto.exercises.map(e => ({
      exerciseKey: e.exercise_key,
      totalTimeInExerciseInS: e.total_time_in_exercise_in_s,
      activeTimeInExerciseInS: e.active_time_in_exercise_in_s,
      laterality: e.laterality,
      hasLaterality: e.has_laterality,
    })),
  }),
};

const sessionBlockMapper: DomainMapper<SessionBlock, SessionBlockDTO> = {
  toDomain: sessionBlockDTO => {
    return {
      mediaBlockName: sessionBlockDTO.media_block_name,
      scripts: sessionBlockDTO.scripts.map(mediaScriptMapper.toDomain),
      parameters: sessionBlockParametersMapper.toDomain(sessionBlockDTO.parameters),
    };
  },
};

const sessionBlockParametersMapper: DomainMapper<SessionBlockParameters, SessionBlockParametersDTO> = {
  toDomain: sessionBlockParametersDTO => ({
    musicMode: sessionBlockParametersDTO.music_mode,
    exerciseTemplateKey: sessionBlockParametersDTO.exercise_template_key,
    breakDurationInS: sessionBlockParametersDTO.break_duration_in_s,
    numberOfSets: sessionBlockParametersDTO.number_of_sets,
    setNumber: sessionBlockParametersDTO.set_number,
  }),
};

const mediaScriptMapper: DomainMapper<MediaScript, MediaScriptDTO> = {
  toDomain: mediaScriptDTO => ({
    key: mediaScriptDTO.key,
    followOrder: mediaScriptDTO.follow_order,
    exitEvent: mediaScriptDTO.exit_event,
    defaultNode: mediaScriptDTO.default_node,
    repeatVideo: mediaScriptDTO.repeat_video,
    nodes: mediaScriptDTO.nodes.map(mediaNodeMapper.toDomain),
  }),
};

const mediaNodeMapper: DomainMapper<MediaNode, MediaNodeDTO> = {
  toDomain: mediaNodeDTO => ({
    key: mediaNodeDTO.key,
    videoNodes: mediaNodeDTO.video_nodes ? mediaNodeDTO.video_nodes.map(videoNodeMapper.toDomain) : [],
    audioNodes: mediaNodeDTO.audio_nodes ? mediaNodeDTO.audio_nodes.map(audioNodeMapper.toDomain) : [],
    textNodes: mediaNodeDTO.text_nodes ? mediaNodeDTO.text_nodes.map(textNodeMapper.toDomain) : [],
    priority: mediaNodeDTO.priority ?? 10,
    triggerEvents: mediaNodeDTO.trigger_events,
  }),
};

const videoNodeMapper: DomainMapper<VideoNode, VideoNodeDTO> = {
  toDomain: videoNodeDTO => ({
    mediaUrl: videoNodeDTO.media_url,
    muteAudio: videoNodeDTO.mute_audio,
    subtitlesUrl: videoNodeDTO.subtitles_url,
    zoomType: videoNodeDTO.zoom_type,
    nodeName: videoNodeDTO.node_name,
  }),
};

const audioNodeMapper: DomainMapper<AudioNode, AudioNodeDTO> = {
  toDomain: audioNodeDTO => ({
    mediaUrl: audioNodeDTO.media_url,
    nodeName: audioNodeDTO.node_name,
  }),
};

const textNodeMapper: DomainMapper<TextNode, TextNodeDTO> = {
  toDomain: textNodeDTO => ({
    text: textNodeDTO.text,
    fontStyleKey: textNodeDTO.font_style_key,
    boxStyleKey: textNodeDTO.box_style_key,
    backgroundStyleKey: textNodeDTO.background_style_key,
    nodeName: textNodeDTO.node_name,
  }),
};

export const sessionStatusResponseMapper: DomainMapper<SessionStatusResponse, SessionStatusResponseDTO> = {
  toDomain: sessionStatusResponseDTO => ({
    sessionId: sessionStatusResponseDTO.session_log_id,
    status: sessionStatusResponseDTO.status,
  }),
};

export const musicSourcesMapper: DomainMapper<MusicSources, MusicSourcesDTO> = {
  toDomain: musicSourcesDTO => ({
    musicSources: musicSourcesDTO.sources,
  }),
};
